import { Injectable, NgZone, inject, signal } from '@angular/core';
import { ComponentNode, extractOrigin } from './component-inspector.types';
import { RenderTrackerService } from './render-tracker.service';

/** Debounce time (ms) after a DOM mutation before we rescan. */
const SCAN_DEBOUNCE_MS = 500;

/**
 * Walks the live DOM using Angular's `ng.getComponent()` debug API and builds
 * a tree of component nodes.  Scanning is driven by a MutationObserver that
 * only runs while the inspector tab is active.
 */
@Injectable({ providedIn: 'root' })
export class ComponentInspectorService {
  private readonly ngZone = inject(NgZone);
  private readonly renderTracker = inject(RenderTrackerService);

  private readonly _tree = signal<ComponentNode[]>([]);
  readonly tree = this._tree.asReadonly();

  private readonly _scanning = signal(false);
  readonly scanning = this._scanning.asReadonly();

  private observer: MutationObserver | null = null;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;
  private active = false;
  private idCounter = 0;

  /** Called when the inspector tab becomes visible / hidden. */
  setActive(active: boolean): void {
    if (active === this.active) return;
    this.active = active;
    if (active) {
      this.scan();
      this.startObserving();
      this.renderTracker.startUiRefresh();
    } else {
      this.stopObserving();
      this.renderTracker.stopUiRefresh();
    }
  }

  /** Force a manual rescan. */
  scan(): void {
    this._scanning.set(true);
    this.idCounter = 0;

    const ng = (window as any).ng;
    if (!ng?.getComponent) {
      this._tree.set([]);
      this._scanning.set(false);
      return;
    }

    const roots = this.buildTree(document.body, ng, 0);
    this._tree.set(roots);
    this._scanning.set(false);
  }

  // ── DOM walking ────────────────────────────────────────────────────────────

  private buildTree(root: Element, ng: any, depth: number): ComponentNode[] {
    const nodes: ComponentNode[] = [];
    // Walk direct element children
    const children = root.children;
    for (let i = 0; i < children.length; i++) {
      const el = children[i] as HTMLElement;
      // Skip the dev-tools own elements to avoid self-inspection noise.
      if (this.isDevToolsElement(el)) continue;

      const cmp = this.tryGetComponent(ng, el);
      if (cmp) {
        this.renderTracker.instrument(cmp);
        const className = cmp.constructor?.name;
        const node: ComponentNode = {
          id: `cmp-${this.idCounter++}`,
          name: className || el.tagName.toLowerCase(),
          element: el,
          children: this.buildTree(el, ng, depth + 1),
          depth,
          origin: extractOrigin(el.tagName, className, el),
        };
        nodes.push(node);
      } else {
        // Not a component host — keep walking deeper.
        const deeper = this.buildTree(el, ng, depth);
        nodes.push(...deeper);
      }
    }
    return nodes;
  }

  private tryGetComponent(ng: any, el: Element): any {
    try {
      return ng.getComponent(el);
    } catch {
      return null;
    }
  }

  private isDevToolsElement(el: HTMLElement): boolean {
    const tag = el.tagName.toLowerCase();
    return tag.startsWith('app-dev-') || tag === 'app-dev-tools';
  }

  // ── MutationObserver ───────────────────────────────────────────────────────

  private startObserving(): void {
    if (this.observer) return;
    this.ngZone.runOutsideAngular(() => {
      this.observer = new MutationObserver(() => this.scheduleScan());
      this.observer.observe(document.body, {
        childList: true,
        subtree: true,
      });
    });
  }

  private stopObserving(): void {
    this.observer?.disconnect();
    this.observer = null;
    if (this.debounceTimer) {
      clearTimeout(this.debounceTimer);
      this.debounceTimer = null;
    }
  }

  private scheduleScan(): void {
    if (this.debounceTimer) clearTimeout(this.debounceTimer);
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      if (this.active) this.scan();
    }, SCAN_DEBOUNCE_MS);
  }

  // ── Tree helpers (used by the panel for smart-collapse) ────────────────────

  /** Compute the max depth of a subtree rooted at `node`. */
  static subtreeDepth(node: ComponentNode): number {
    if (node.children.length === 0) return 0;
    let max = 0;
    for (const child of node.children) {
      max = Math.max(max, 1 + ComponentInspectorService.subtreeDepth(child));
    }
    return max;
  }

  /** Collect all node IDs in the tree. */
  static allIds(nodes: ComponentNode[]): string[] {
    const ids: string[] = [];
    const walk = (list: ComponentNode[]) => {
      for (const n of list) {
        ids.push(n.id);
        walk(n.children);
      }
    };
    walk(nodes);
    return ids;
  }
}
